import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftRight, ArrowRight, DollarSign, Shield, Ship, Factory, TrendingDown, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import api from '@/lib/api';
import { AnalyticsShell } from '@/components/AnalyticsShell';

const GLOBAL_COUNTRIES = [
  { code: 'CN', name: 'China' }, { code: 'VN', name: 'Vietnam' }, { code: 'ID', name: 'Indonesia' },
  { code: 'TH', name: 'Thailand' }, { code: 'MY', name: 'Malaysia' }, { code: 'PH', name: 'Philippines' },
  { code: 'IN', name: 'India' }, { code: 'BD', name: 'Bangladesh' }, { code: 'KH', name: 'Cambodia' },
  { code: 'JP', name: 'Japan' }, { code: 'KR', name: 'South Korea' }, { code: 'TW', name: 'Taiwan' },
  { code: 'MM', name: 'Myanmar' }, { code: 'SG', name: 'Singapore' }, { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' }, { code: 'DE', name: 'Germany' }, { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' }, { code: 'TR', name: 'Turkey' }, { code: 'PL', name: 'Poland' },
];

const RISK_COLORS = { very_high: '#8B0000', high: '#FF3B30', moderate: '#FF9500', low: '#2E7D32', very_low: '#0097A7', unknown: '#4E6974' };

const ComparativeSourcingPage = () => {
  const [selectedCountry, setSelectedCountry] = useState('CN');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.getSourcingCompare(selectedCountry)
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedCountry]);

  const current = data?.comparisons?.find(c => c.is_current);
  const alternatives = data?.comparisons?.filter(c => !c.is_current) || [];

  const riskCompareData = data?.comparisons?.map(c => ({
    country: c.country_name,
    'Overall risk': c.overall_risk,
    'Geopolitical': c.geopolitical_risk,
    'Trade': c.trade_risk,
    'Freight': c.freight_risk,
    fill: c.is_current ? '#0D3B4F' : '#0097A7',
  })) || [];

  const radarData = current ? [
    { metric: 'Overall risk', current: current.overall_risk, best: Math.min(...alternatives.map(a => a.overall_risk)) },
    { metric: 'Geopolitical', current: current.geopolitical_risk, best: Math.min(...alternatives.map(a => a.geopolitical_risk)) },
    { metric: 'Trade policy', current: current.trade_risk, best: Math.min(...alternatives.map(a => a.trade_risk)) },
    { metric: 'Freight', current: current.freight_risk, best: Math.min(...alternatives.map(a => a.freight_risk)) },
    { metric: 'Regulatory', current: current.regulatory_risk, best: Math.min(...alternatives.map(a => a.regulatory_risk)) },
    { metric: 'Sanctions', current: current.sanctions_risk, best: Math.min(...alternatives.map(a => a.sanctions_risk)) },
  ] : [];

  return (
    <AnalyticsShell>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="sourcing-title">Comparative sourcing</h1>
            <p className="text-[#4E6974] mt-2">Compare alternative sourcing countries by risk, cost, labour, and freight.</p>
          </div>
          <div className="relative">
            <Button variant="outline" onClick={() => setShowPicker(!showPicker)} className="min-w-[200px] justify-between" data-testid="sourcing-country-picker">
              Comparing: {data?.current_country_name || selectedCountry} <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
            {showPicker && (
              <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-[#E0E0E0] p-2 z-20 w-56 max-h-72 overflow-y-auto">
                {GLOBAL_COUNTRIES.map(c => (
                  <button key={c.code} onClick={() => { setSelectedCountry(c.code); setShowPicker(false); }}
                    className={`w-full px-3 py-2 rounded text-sm text-left transition-all ${c.code === selectedCountry ? 'bg-[#00707C] text-white' : 'hover:bg-[#F5F5F5] text-[#4E6974]'}`}>
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {loading ? <div className="text-center py-20 text-[#4E6974]">Loading comparison...</div> : data && (
        <>
          {/* Comparison table */}
          <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
            <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-[#0D3B4F]">Side-by-side comparison</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sourcing-compare-table">
                  <thead>
                    <tr className="border-b border-[#E0E0E0]">
                      <th className="text-left py-3 px-3 text-[#4E6974] font-medium">Metric</th>
                      {data.comparisons.map(c => (
                        <th key={c.country_code} className={`text-center py-3 px-3 font-medium ${c.is_current ? 'text-[#0D3B4F] bg-[#F5F5F5]' : 'text-[#4E6974]'}`}>
                          {c.country_name} {c.is_current && <Badge className="ml-1 bg-[#0D3B4F] text-white text-[9px]">Current</Badge>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <CompareRow label="Overall risk score" field="overall_risk" data={data.comparisons} format={v => v} colorFn={v => v > 60 ? '#FF3B30' : v > 40 ? '#FF9500' : '#2E7D32'} />
                    <CompareRow label="Geopolitical risk" field="geopolitical_risk" data={data.comparisons} format={v => v} colorFn={v => v > 60 ? '#FF3B30' : v > 40 ? '#FF9500' : '#2E7D32'} />
                    <CompareRow label="Trade policy risk" field="trade_risk" data={data.comparisons} format={v => v} colorFn={v => v > 60 ? '#FF3B30' : v > 40 ? '#FF9500' : '#2E7D32'} />
                    <CompareRow label="Freight risk" field="freight_risk" data={data.comparisons} format={v => v} colorFn={v => v > 60 ? '#FF3B30' : v > 40 ? '#FF9500' : '#2E7D32'} />
                    <CompareRow label="Sanctions risk" field="sanctions_risk" data={data.comparisons} format={v => v} colorFn={v => v > 40 ? '#FF3B30' : v > 20 ? '#FF9500' : '#2E7D32'} />
                    <CompareRow label="Mfg wage (USD/mo)" field="manufacturing_wage_usd" data={data.comparisons} format={v => v ? `$${v.toLocaleString()}` : 'N/A'} />
                    <CompareRow label="Min wage (USD/mo)" field="min_wage_usd" data={data.comparisons} format={v => v ? `$${v}` : 'N/A'} />
                    <CompareRow label="Freight (USD/FEU)" field="freight_rate_usd" data={data.comparisons} format={v => v ? `$${v.toLocaleString()}` : 'N/A'} />
                    <CompareRow label="Safety rating" field="safety_rating" data={data.comparisons} format={v => v || 'N/A'} />
                    <CompareRow label="Allied nation" field="allied_nation" data={data.comparisons} format={v => v ? 'Yes' : 'No'} colorFn={v => v ? '#2E7D32' : '#4E6974'} />
                    <CompareRow label="Modern slavery risk" field="slavery_risk" data={data.comparisons} format={v => (v || 'unknown').replace(/_/g, ' ')} colorFn={v => RISK_COLORS[v] || '#4E6974'} />
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Risk radar */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
              <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-[#0D3B4F]">Risk profile comparison</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[350px]" data-testid="sourcing-radar">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#E0E0E0" />
                      <PolarAngleAxis dataKey="metric" fontSize={11} tick={{ fill: '#4E6974' }} />
                      <PolarRadiusAxis domain={[0, 100]} fontSize={9} tick={{ fill: '#4E6974' }} />
                      <Radar name={current?.country_name} dataKey="current" stroke="#0D3B4F" fill="#0D3B4F" fillOpacity={0.15} strokeWidth={2} />
                      <Radar name="Best alternative" dataKey="best" stroke="#0097A7" fill="#0097A7" fillOpacity={0.15} strokeWidth={2} />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex gap-4 justify-center mt-2">
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#0D3B4F]" /><span className="text-xs text-[#4E6974]">{current?.country_name}</span></div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#00707C]" /><span className="text-xs text-[#4E6974]">Best alternative</span></div>
                </div>
              </CardContent>
            </Card>

            {/* Overall risk bar chart */}
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
              <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-[#0D3B4F]">Overall risk scores</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[350px]" data-testid="sourcing-risk-bar">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={riskCompareData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                      <XAxis dataKey="country" fontSize={11} tick={{ fill: '#4E6974' }} />
                      <YAxis domain={[0, 100]} fontSize={11} tick={{ fill: '#4E6974' }} />
                      <Tooltip />
                      <Bar dataKey="Overall risk" radius={[4, 4, 0, 0]}>
                        {riskCompareData.map((c, i) => <Cell key={i} fill={c.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recommendations */}
          {data.recommendations.length > 0 && (
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-8">
              <CardHeader className="pb-2"><CardTitle className="text-base font-bold text-[#0D3B4F]">Sourcing recommendations</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3" data-testid="sourcing-recommendations">
                  {data.recommendations.map((r, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#F5F5F5]">
                      <ArrowRight className="h-4 w-4 text-[#0097A7] mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-[#0D3B4F]">{r}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </AnalyticsShell>
  );
};

const CompareRow = ({ label, field, data, format, colorFn }) => (
  <tr className="border-b border-[#F5F5F5]">
    <td className="py-3 px-3 text-[#4E6974] font-medium">{label}</td>
    {data.map(c => {
      const val = c[field];
      const display = format ? format(val) : val;
      const color = colorFn ? colorFn(val) : '#0D3B4F';
      return (
        <td key={c.country_code} className={`py-3 px-3 text-center font-semibold ${c.is_current ? 'bg-[#F5F5F5]' : ''}`} style={{ color }}>
          {display}
        </td>
      );
    })}
  </tr>
);

export default ComparativeSourcingPage;
